// Notification Sound Player
// Plays a synthesized notification tone when push notifications arrive.
// Chrome doesn't support the Notification API `sound` property, so we
// play audio from the page context via service worker messaging.

const SOUND_ENABLED_KEY = 'allspark:notification_sound_enabled'

export function isNotificationSoundEnabled(): boolean {
  try {
    const val = localStorage.getItem(SOUND_ENABLED_KEY)
    return val === null ? true : val === 'true'
  } catch {
    return true
  }
}

export function setNotificationSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_ENABLED_KEY, String(enabled))
  } catch { /* ignore */ }
}

let audioContext: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  if (!audioContext) {
    try {
      audioContext = new AudioContext()
    } catch {
      return null
    }
  }
  return audioContext
}

/**
 * Plays a short two-tone chime (like a typical notification sound).
 */
export function playNotificationSound(): void {
  const ctx = getAudioContext()
  if (!ctx) return

  // Resume context if suspended (autoplay policy)
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {})
  }

  const now = ctx.currentTime

  // Soft two-tone chime: C4 (262 Hz) → E4 (330 Hz), low and gentle
  const gain1 = ctx.createGain()
  gain1.connect(ctx.destination)
  gain1.gain.setValueAtTime(0.12, now)
  gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.8)

  const osc1 = ctx.createOscillator()
  osc1.type = 'sine'
  osc1.frequency.setValueAtTime(262, now)
  osc1.connect(gain1)
  osc1.start(now)
  osc1.stop(now + 0.3)

  const gain2 = ctx.createGain()
  gain2.connect(ctx.destination)
  gain2.gain.setValueAtTime(0.12, now + 0.2)
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 1.0)

  const osc2 = ctx.createOscillator()
  osc2.type = 'sine'
  osc2.frequency.setValueAtTime(330, now + 0.2)
  osc2.connect(gain2)
  osc2.start(now + 0.2)
  osc2.stop(now + 0.5)
}

// Listen for messages from the service worker
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_SOUND' && isNotificationSoundEnabled()) {
      playNotificationSound()
    }
  })
}
